import i18next from 'i18next';
import { toast } from 'sonner';

import { downloadBlob } from '../download-blob.js';
import { useOperationalExecutionStore } from '../../store/operational-execution.store.js';

const OPERATIONAL_RUN_ERROR_I18N = {
    MISSING_COMMANDER: {
        key: 'operationalExecution:errors.missingCommander',
        defaultValue: 'A Crisis Commander must be assigned. Linked team leaders are used by default when the role is empty.',
    },
    MISSING_FACILITATOR_OR_OWNER: {
        key: 'operationalExecution:errors.missingFacilitatorOrOwner',
        defaultValue: 'A facilitator or owner must be assigned. Linked team leaders are used by default when the role is empty.',
    },
    ACTIVE_RUN_EXISTS: {
        key: 'operationalExecution:errors.activeRunExists',
        defaultValue: 'An active execution already exists for this source.',
    },
    SOURCE_CLOSED_CRISIS: {
        key: 'operationalExecution:errors.sourceClosedCrisis',
        defaultValue: 'Execution cannot be started for a closed crisis.',
    },
    SOURCE_CLOSED_EXERCISE: {
        key: 'operationalExecution:errors.sourceClosedExercise',
        defaultValue: 'Execution cannot be started for a closed exercise.',
    },
    MISSING_PARTY_TEMPLATE: {
        key: 'operationalExecution:errors.missingPartyTemplate',
        defaultValue: 'Operational execution cannot start until the default ownership template is available.',
    },
    MISSING_RR_ASSOCIATION: {
        key: 'operationalExecution:errors.missingRrAssociation',
        defaultValue: 'Link at least one team on the Roles & Responsibilities table before starting execution.',
    },
    MISSING_EXECUTION_TASKS: {
        key: 'operationalExecution:errors.missingExecutionTasks',
        defaultValue: 'Linked teams must have at least one execution task before starting execution.',
    },
    DUPLICATE_TASK_KEY: {
        key: 'operationalExecution:errors.duplicateTaskKey',
        defaultValue: 'The same task key appears more than once in one team assignment.',
    },
    MISSING_TASK_TIMING: {
        key: 'operationalExecution:errors.missingTaskTiming',
        defaultValue: 'Some execution tasks are missing a target completion. Fill the durations on the R&R table before starting.',
    },
};

const METHOD_ALIASES = {
    start: ['start', 'startRun', 'create'],
    listForSource: ['listForSource', 'getForSource', 'listBySource', 'getAll'],
    getBoard: ['getBoard', 'board', 'getById'],
    getHistory: ['getHistory', 'history', 'listHistory'],
    startTask: ['startTask', 'beginTask'],
    completeTask: ['completeTask', 'finishTask'],
    listTaskEvidence: ['listTaskEvidence', 'getTaskEvidence'],
    listUpdates: ['getUpdates', 'listUpdates'],
    uploadTaskEvidence: ['uploadTaskEvidence', 'addTaskEvidence'],
    downloadTaskEvidence: ['downloadTaskEvidence', 'getTaskEvidenceFile'],
    appendUpdate: ['appendUpdate', 'addUpdate'],
    overrideDependency: ['overrideDependency', 'overrideTaskDependency', 'overrideDependencies'],
    reassignTask: ['reassignTask', 'assignTask'],
    pause: ['pause', 'pauseRun'],
    resume: ['resume', 'resumeRun'],
    complete: ['complete', 'completeRun'],
    cancel: ['cancel', 'cancelRun'],
};

function unwrap(response) {
    if (response?.data?.data !== undefined) return response.data.data;
    if (response?.data !== undefined) return response.data;
    return response;
}

export class OperationalExecutionIntegration {
    constructor(serviceResolver) {
        this.serviceResolver = serviceResolver;
        this.boardRequestSequence = 0;
        this.historyRequestSequence = 0;
    }

    _service() {
        const service = typeof this.serviceResolver === 'function'
            ? this.serviceResolver()
            : this.serviceResolver;
        if (!service) {
            throw new Error(i18next.t('operationalExecution:errors.serviceUnavailable', {
                defaultValue: 'Operational execution service is not available yet.',
            }));
        }
        return service;
    }

    async _call(operation, args) {
        const service = this._service();
        const methodName = METHOD_ALIASES[operation].find((name) => typeof service[name] === 'function');
        if (!methodName) {
            throw new Error(i18next.t('operationalExecution:errors.unsupportedOperation', {
                operation,
                defaultValue: 'The operational execution service does not support {{operation}}.',
            }));
        }
        return service[methodName](...args);
    }

    async listForSource(sourceType, sourceId, options = {}) {
        const store = useOperationalExecutionStore.getState();
        if (!options.silent) {
            store.setLoadingRuns(true);
            store.clearError();
        }
        try {
            const service = this._service();
            const response = typeof service.listForSource === 'function'
                ? await service.listForSource(sourceType, sourceId)
                : await this._call('listForSource', [{ sourceType, sourceId }]);
            const payload = unwrap(response);
            const runs = Array.isArray(payload) ? payload : payload?.items || payload?.runs || [];
            store.setRuns(runs);
            const currentId = store.selectedRunId;
            const selected = runs.find((run) => String(run._id || run.id) === String(currentId))
                || runs.find((run) => !['completed', 'cancelled', 'canceled', 'closed'].includes(String(run.status).toLowerCase()))
                || runs[0];
            store.setSelectedRunId(selected?._id || selected?.id || null);
            return { success: true, data: runs, selectedRunId: selected?._id || selected?.id || null };
        } catch (error) {
            const message = this._errorMessage(error);
            store.setError(message);
            if (!options.silent) toast.error(message);
            return { success: false, error: message };
        } finally {
            if (!options.silent) store.setLoadingRuns(false);
        }
    }

    async getBoard(runId, options = {}) {
        const store = useOperationalExecutionStore.getState();
        const requestToken = ++this.boardRequestSequence;
        const sourceKey = store.sourceKey;
        if (!options.silent) {
            store.setLoadingBoard(true);
            store.clearError();
        }
        try {
            const response = await this._call('getBoard', [runId]);
            const board = unwrap(response);
            if (!this._isCurrentRunRequest(requestToken, this.boardRequestSequence, sourceKey, runId)) {
                return { success: true, data: board, stale: true };
            }
            store.setBoard(board);
            return { success: true, data: board };
        } catch (error) {
            const message = this._errorMessage(error);
            if (this._isCurrentRunRequest(requestToken, this.boardRequestSequence, sourceKey, runId)) {
                store.setError(message);
                if (!options.silent) toast.error(message);
            }
            return { success: false, error: message };
        } finally {
            if (this._isCurrentRunRequest(requestToken, this.boardRequestSequence, sourceKey, runId)) {
                store.setLoadingBoard(false);
            }
        }
    }

    async getHistory(runId, options = {}) {
        const store = useOperationalExecutionStore.getState();
        const requestToken = ++this.historyRequestSequence;
        const sourceKey = store.sourceKey;
        if (!options.silent) {
            store.setLoadingHistory(true);
            store.setHistoryError(null);
        }
        try {
            const response = await this._call('getHistory', [runId]);
            const payload = unwrap(response);
            const history = Array.isArray(payload) ? payload : payload?.items || payload?.history || [];
            if (!this._isCurrentRunRequest(requestToken, this.historyRequestSequence, sourceKey, runId)) {
                return { success: true, data: history, stale: true };
            }
            store.setHistory(history);
            return { success: true, data: history };
        } catch (error) {
            const message = this._errorMessage(error);
            if (this._isCurrentRunRequest(requestToken, this.historyRequestSequence, sourceKey, runId)) {
                store.setHistoryError(message);
                if (!options.silent) toast.error(message);
            }
            return { success: false, error: message };
        } finally {
            if (this._isCurrentRunRequest(requestToken, this.historyRequestSequence, sourceKey, runId)) {
                store.setLoadingHistory(false);
            }
        }
    }

    async start(sourceType, sourceId, body = {}) {
        const service = this._service();
        const canonicalType = String(sourceType).toLowerCase().replaceAll('-', '');
        const startMethod = canonicalType === 'crisisevent' && typeof service.startFromCrisis === 'function'
            ? 'startFromCrisis'
            : canonicalType === 'exercise' && typeof service.startFromExercise === 'function'
                ? 'startFromExercise'
                : null;
        const operation = startMethod || 'start';
        const args = startMethod ? [sourceId, body] : [sourceType, sourceId, body];
        return this._mutate(operation, args, 'messages.started', 'Execution started', {
            after: async (data) => {
                const runId = data?._id || data?.id || data?.run?._id || data?.run?.id;
                await this.listForSource(sourceType, sourceId, { silent: true });
                if (runId) {
                    await Promise.all([
                        this.getBoard(runId, { silent: true }),
                        this.getHistory(runId, { silent: true }),
                    ]);
                }
            },
        });
    }

    startTask(runId, taskId) {
        return this._runMutation('startTask', [runId, taskId], runId, 'messages.taskStarted', 'Task started');
    }

    completeTask(runId, taskId, body = {}) {
        return this._runMutation('completeTask', [runId, taskId, body], runId, 'messages.taskCompleted', 'Task completed');
    }

    async listTaskEvidence(runId, taskId) {
        try {
            const response = await this._call('listTaskEvidence', [runId, taskId]);
            return { success: true, data: unwrap(response) || [] };
        } catch (error) {
            const message = this._errorMessage(error);
            toast.error(message);
            return { success: false, error: message, data: [] };
        }
    }

    async listUpdates(runId, params = {}) {
        try {
            const response = await this._call('listUpdates', [runId, params]);
            const payload = unwrap(response);
            const items = Array.isArray(payload) ? payload : payload?.items || payload?.updates || [];
            return { success: true, data: items };
        } catch (error) {
            const message = this._errorMessage(error);
            toast.error(message);
            return { success: false, error: message, data: [] };
        }
    }

    uploadTaskEvidence(runId, taskId, file) {
        return this._runMutation(
            'uploadTaskEvidence',
            [runId, taskId, file],
            runId,
            'messages.evidenceUploaded',
            'Evidence uploaded',
        );
    }

    async downloadTaskEvidence(runId, taskId, evidence) {
        try {
            const evidenceId = evidence?._id || evidence?.id;
            const response = await this._call('downloadTaskEvidence', [runId, taskId, evidenceId]);
            const blob = response instanceof Blob ? response : new Blob([response], {
                type: evidence?.mimeType || 'application/octet-stream',
            });
            downloadBlob(blob, evidence?.originalFileName || 'evidence');
            return { success: true };
        } catch (error) {
            const message = this._errorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    appendUpdate(runId, body = {}) {
        return this._runMutation('appendUpdate', [runId, body], runId, 'messages.updateAdded', 'Update added');
    }

    overrideDependency(runId, taskId, body = {}) {
        return this._runMutation('overrideDependency', [runId, taskId, body], runId, 'messages.dependencyOverridden', 'Dependency overridden');
    }

    reassignTask(runId, taskId, body = {}) {
        return this._runMutation('reassignTask', [runId, taskId, body], runId, 'messages.taskReassigned', 'Task reassigned');
    }

    pause(runId, body = {}) {
        return this._runMutation('pause', [runId, body], runId, 'messages.paused', 'Execution paused');
    }

    resume(runId, body = {}) {
        return this._runMutation('resume', [runId, body], runId, 'messages.resumed', 'Execution resumed');
    }

    complete(runId, body = {}) {
        return this._runMutation('complete', [runId, body], runId, 'messages.completed', 'Execution completed');
    }

    cancel(runId, body = {}) {
        return this._runMutation('cancel', [runId, body], runId, 'messages.cancelled', 'Execution cancelled');
    }

    async _runMutation(operation, args, runId, messageKey, defaultValue) {
        return this._mutate(operation, args, messageKey, defaultValue, {
            after: () => this._refreshRunIfCurrent(runId),
        });
    }

    async _refreshRunIfCurrent(runId) {
        if (String(useOperationalExecutionStore.getState().selectedRunId || '') !== String(runId)) {
            return;
        }
        await Promise.all([
            this.getBoard(runId, { silent: true }),
            this.getHistory(runId, { silent: true }),
        ]);
    }

    _isCurrentRunRequest(requestToken, currentToken, sourceKey, runId) {
        const current = useOperationalExecutionStore.getState();
        return requestToken === currentToken
            && current.sourceKey === sourceKey
            && String(current.selectedRunId || '') === String(runId || '');
    }

    async _mutate(operation, args, messageKey, defaultValue, options = {}) {
        const store = useOperationalExecutionStore.getState();
        store.setMutating(true);
        store.clearError();
        try {
            const response = METHOD_ALIASES[operation]
                ? await this._call(operation, args)
                : await this._service()[operation](...args);
            const data = unwrap(response);
            toast.success(i18next.t(`operationalExecution:${messageKey}`, { defaultValue }));
            if (options.after) await options.after(data);
            return { success: true, data };
        } catch (error) {
            const message = this._errorMessage(error);
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setMutating(false);
        }
    }

    _errorMessage(error) {
        const code = error?.code
            || error?.response?.data?.code
            || error?.details?.code;
        const mapped = OPERATIONAL_RUN_ERROR_I18N[code];
        if (mapped) {
            return i18next.t(mapped.key, { defaultValue: mapped.defaultValue }) || mapped.defaultValue;
        }
        return i18next.t('operationalExecution:errors.generic', {
            defaultValue: 'Operational execution action failed.',
        }) || 'Operational execution action failed.';
    }
}
